<template>
  <div>
    <div id="preview">

      <cover-page-condensed
        :project-info="projectInfo"
        :product-brand="productBrand"
      />

      <!--////////////////////////////
      /// Project Scope Page ///
      ////////////////////////////////-->
      <project-scope-condensed 
        :product-brand="productBrand"
        :proposal="proposal"
      />

      <!--////////////////////////////
      /// section loops begin here ///
      ////////////////////////////////-->

      <template
        v-for="(section, i) in proposalContent"
      >

        <section
          class="content-section"
          v-if="section.type != 'optional' && 
            (!Object.keys(section).includes('visible') || section.visible) && 
            (!!section.d_component || section.type === 'pricing')"
          :key="section.name + '-' + i"
        >
          <div class="running-header">
            <div class="running-header__top-border"></div>
            <div class="running-header__text">
              <div>{{ productBrand }} Condensed Budget Proposal</div>
            </div>
          </div>

          <div class="running-site">
            <a 
              class="px-2" 
              :href="productBrand === 'Mitrex' ? 'https://www.mitrex.com' : 'https://www.cladify.com'" 
              target="_blank" 
              rel="noreferrer noopener"
            >
              {{ productBrand === 'Mitrex' ? 'mitrex.com' : 'cladify.com' }}
            </a>
          </div>

          <div class="running-footer">
            {{ projectInfo.address.value }}
          </div>

          <div class="running-logo">
            <img 
              class="running-logo__image"
              src="../../assets/mitrex_and_cladify_logo.png" 
              alt="mitrex and cladify logo" 
            />
          </div>

          <!-- static sections -->
          <template v-if="section.type == 'static'">

            <template 
              v-for="(subsection, i) in section.subsections">

              <div
                class="content-subsection"
                v-bind="subsection.d_bind"
                v-if="subsection.value.length > 0 && Array.isArray(subsection.value) 
                  ? subsection.value.filter(d => !Object.keys(d).includes('visible') || d.visible == true).length > 0
                  : !!subsection.value && subsection.value != ''"
                :key="subsection + '-' + i">

                <h1>{{ section.name }}</h1>
                <h2>{{ subsection.label }}</h2>

                <template v-if="subsection.d_component == 'VList'">
                  <div class="content">
                    <ul>
                      <li
                        v-for="value in subsection.value"
                        :key="value">
                        {{ value }}
                      </li>
                    </ul>
                  </div>
                </template>

                <template v-if="subsection.d_component == 'VSubsection'">

                  <div
                    class="content-sub-subsection"
                    v-for="(subSubsection, i) in subsection.value"
                    :key="i">

                    <template v-if="subSubsection.visible || 
                      !Object.keys(subSubsection).includes('visible')">

                      <template v-for="(template, i) in subsection.template">

                        <component
                          :key="template.label + '-' + i"
                          :is="template.tag"
                          v-if="template.tag != 'img'">
                          {{ subSubsection[template.label] }}
                        </component>

                        <img
                          v-if="template.tag == 'img' && subSubsection[template.label].url"
                          :key="template.label + '-' + i"
                          :class="template.display_class"
                          :src="subSubsection[template.label].url"/>

                      </template>
                    
                    </template>

                  </div>

                  <signature-line 
                    class="mt-5"
                    v-if="subsection.id == 'drawing_highlight'"
                    :section-name="subsection.label"
                    :has-name="false"
                    :has-signature="false"
                    :has-initial="true"/>
                </template>

                <template v-if="subsection.d_component == 'DynamicMaterials'">
                  <div class="break-after">
                    <dynamic-materials
                      :template="subsection.template" 
                      :materials="subsection.value"/>
                    <small>**Use as reference only. Colors, patterns, and shades to be approved during the sample approval phase.</small>
                  </div>
                </template>

                <template v-if="subsection.d_component == 'TextAndImageD'">
                  <div class="content">
                    <p>{{ subsection.value.text }}</p>
                    <img :src="subsection.value.image.url"/>
                  </div>
                </template>

                <template v-if="subsection.d_component == 'VRichTextRenderer'">
                  <v-rich-text-renderer 
                    :value="subsection.value" 
                    :replacements="replacements" 
                  />
                </template>

              </div>
            </template>

            <div 
              v-if="section.id == 'general_tos' && section.visible"
              class="mt-6">
              <div>{{ productBrand }} Signature:</div>
              <signature-line 
                v-if="section.id == 'general_tos'"
                class="mt-2 mb-5"
                :section-name="section.name"
                has-date
              />
              <div>Client Signature:</div>
              <signature-line 
                v-if="section.id == 'general_tos'"
                class="mt-2"
                :section-name="section.name"
                has-date
              />
            </div> 

          </template>
          <!-- static sections end -->

          <!-- dynamic sections -->
          <template v-if="section.type == 'dynamicSubsections'">

            <template v-if="!!section.d_component && section.d_component !== 'VSubsection'">
              <component
                v-for="(subsection, i) in section.subsections"
                :key="i"
                :subsection="subsection"
                :is="section.d_component"
              />
            </template>
            
            <template v-else>
              <div
                class="content-subsection"
                v-for="(subsection, i) in section.subsections"
                :key="i">

                <h1>{{ section.name }}</h1>

                <template 
                  v-for="(template, i) in section.subsection_template">

                  <component
                    :key="template.label + '-' + i"
                    :is="template.tag"
                    v-if="template.tag != 'img'"
                  >
                    {{ subsection[template.label] }}
                  </component>

                  <img
                    v-if="template.tag == 'img' && subsection[template.label].url"
                    :key="template.label + '-' + i"
                    :class="template.display_class"
                    :src="subsection[template.label].url"/>

                </template>

              </div>
            </template>


          </template>
          <!-- dynamic sections end -->

          <template v-if="section.type == 'pricing'">

            <div
              class="content-subsection"
              v-for="(subsection, i) in section.subsections"
              :key="i"
            >
              <component
                :is="subsection.find(s => s.id === 'content').display.component"
                :rfq="rfq"
                :estimation="estimation"
                :info="subsection"
                :replacements="replacements"
                :proposal="proposal"
              />

              <!-- <signature-line 
                v-if="section.id == 'pricing_summary' || 
                  (section.id == 'detailed_pricing' && (i + 1) == section.subsections.length)"
                :class="section.id == 'pricing_summary' ? 'mt-4' : 'mt-5'"
                :section-name="`${productBrand == 'Mitrex' ? 
                  filters.Uppercase(subsection.filter(
                    d => d.id == 'content')[0].content_id) + ' ' + section.name : 
                  section.name}`"
                :has-name="false"
                :has-signature="false"
                :has-initial="true"/> -->

            </div>

          </template>

        </section>

        <component
          class="content-section"
          v-if="(section.type == 'optional' && section.visible == true)"
          :key="section.name + '-' + i"
          :is="section.component"
          v-bind="section.bind"
          :rfq="rfq"
          :has-mpa-price="proposal.project_info.pricing ? proposal.project_info.pricing.has_mpa_price : false"
          :has-dpa-price="proposal.project_info.pricing ? proposal.project_info.pricing.has_dpa_price : false"
          :phone="projectInfo.sales_person.value.Phone"
          :email="projectInfo.sales_person.value.Email"
          :product-brand="productBrand"
          :proposal-info="proposalInfo"
          :project-info="projectInfo"
        />

      </template>

    </div>

    <div id="preview-output" :class="`${productBrand.toLowerCase()} proposal--condensed`">
    </div>

  </div>
</template>

<script>
import VRichTextRenderer from '@/components/base/VRichTextRenderer'
import PricingSummaryCondensed from './PricingSummaryCondensed'
import DynamicMaterials from './DynamicMaterials'
import CoverPageCondensed from './CoverPageCondensed'
import InclusionsAndExclusions from './InclusionsAndExclusions'
import PastProjectsCondensed from './PastProjectsCondensed'
import LastPageCondensed from './LastPageCondensed'
import ProjectScopeCondensed from './ProjectScopeCondensed'
import InstallationMethodCondensed from './InstallationMethodCondensed'
import AlternateOptionCondensed from './AlternateOptionCondensed'

import SignatureLine from '@/components/proposalmaker/SignatureLine'

import { Previewer, registerHandlers } from "pagedjs"
import RepeatTableHeadersHandler from "@/helpers/repeatTableHeaders.js"
import condensedProposalStyle from '@/helpers/condensedProposalStyle'

import { format, parseJSON } from 'date-fns'
import filters from '@/filters'

export default {
  props: {
    proposal: {
      type: Object,
      required: true
    },
    rfq: {
      type: Object
    },
    installationMethods: {
      type: Array
    },
    productBrand: {
      type: String,
      required: true
    }
  },
  components: {
    VRichTextRenderer, PricingSummaryCondensed, CoverPageCondensed, 
    PastProjectsCondensed, InclusionsAndExclusions, LastPageCondensed, 
    DynamicMaterials, SignatureLine, ProjectScopeCondensed,
    InstallationMethodCondensed, AlternateOptionCondensed
  },
  data() {
    return {
      formatDate: (date) => {
        return date ? format(date, "MM/dd/yyyy") : null;
      },
      parseDate: (date) => { return date ? parseJSON(date) : date },
      filters: filters
    }
  },
  computed: {
    projectInfo() {
      return this.proposal.project_info;
    },
    proposalInfo() {
      return this.proposal.proposal_info;
    },
    proposalContent() {
      return this.proposal.proposal_content;
    },
    estimation({ rfq }) {
      return rfq.c_estimation ? rfq.c_estimation : rfq.estimation;
    },
    replacements() {
      const res = [];

      return res
    }
  },
  mounted() {
    this.preview();
  },
  methods: {
    preview() {
      registerHandlers(RepeatTableHeadersHandler);
      const previewer = new Previewer();

      var fragment = new DocumentFragment();
      let previewEls = [].slice.call(document.querySelector("#preview").childNodes);

      previewEls.forEach(child => {
        fragment.appendChild(child);
      })

      previewer
        .preview(
          fragment,
          [{"inline": condensedProposalStyle}],
          document.querySelector("#preview-output")
        )
        .then(flow => {
          console.log("preview rendered, total pages", flow.total, { flow });
        });
    }
  }
}
</script>

<style scoped>
@import '../../styles/condensed-proposal.css';
@import '../../styles/interface.css';
</style>
